import { React, useState, useMemo, useContext, useRef } from 'react'
import { Link, useParams, useLocation } from 'react-router-dom';
import moment from "moment";
import DataTable from '../../DataTables/DataTable';
import ExcelDownloader from "../../shared/ExcelDownloader";
import { exportExcel, getInvestorFundsUrl } from "../../../Config/config";
import { routeNames } from '../../../routeSegments';
import { UserContext } from '../../../Contexts/UserContext';
import useModule from '../../../hooks/useModule';
import Constants from '../../../Constants';
import OverflowToolTip from '../../shared/OverflowToolTip';
import { DataFoundContext } from '../../../Contexts/DataFoundContext';


const InvestorFunds = ({funds, investorName}) => {
    const InvFundsRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const { isStudent, isDirectoryUser ,isTrialExport} = useContext(UserContext);
    const { investorId } = useParams();
    const [showXlDownload, setshowXlDownload] = useState(false);
    const location = useLocation();
    const { primaryMasterId } = useModule();
    const { investorsFundsDataFound } =
    useContext(DataFoundContext);


  /* The `customStringSortMemo` constant is a memoized function that is used for sorting the data in
  the data table in the `InvestorFunds` component. */
    const customStringSortMemo = useMemo(() => {
        return (rowA, rowB, columnId, desc) => {
            const defaultVal = desc ? '--' : 'A';
            return (rowA?.values[columnId]?.name ?? defaultVal).localeCompare(rowB?.values[columnId]?.name ?? defaultVal);
        }
    }, []);

   /* The `dealDateSort` constant is a memoized function that is used for sorting the data in the
   "Date" column of the data table in the `InvestorFunds` component. */
    const dealDateSort = useMemo(() => {
        return (rowA, rowB, columnId, desc) => {
            return moment.utc(rowA?.values[columnId].date).diff(moment.utc(rowB?.values[columnId].date))
        }
    }, [])

   /**
    * The function "addCommasToNumber" takes a number as input and returns a string representation of
    * the number with commas added for thousands separators.
    * @param number - The `number` parameter is the number that you want to add commas to.
    * @returns a string representation of the number with commas added for every three digits.
    */
    const addCommasToNumber = (number) => {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };

      
   /* The `columns` constant is an array of objects that define the columns for the data table in the
   `InvestorFunds` component. Each object represents a column and contains properties such as
   `Header` (the column header text), `accessor` (the key to access the data for that column in each
   row), `className` (the CSS class for the column), and `Cell` (a function that returns the content
   for each cell in the column). */
    const columns = useMemo(() => [
        {
            Header: "Fund Name",
            accessor: "fundName",
            className: "w-[13%] clickable lg:relative text-left sticky left-0  bg-white",
            headerspanClassName: "",
            headerClassName: "text-left lg:relative sticky left-0   w-[3rem] bg-[#EDEDED] ",
            Cell: (cell) => {
                return <OverflowToolTip component={  
                            <Link className='hover:text-[#BC8B20]' 
                                state={{ prevPath: location.state?.prevPath ?? "" }} 
                                to={primaryMasterId == Constants.primaryMasterIds.peId ? routeNames.pe_investors_funds_page(cell.value.id) : routeNames.vc_investors_funds_page(cell.value.id)}
                                target='_blank'>
                                    {cell?.value?.name ? cell?.value?.name : ''}
                            </Link>
                        }/>

            },
            sortType: customStringSortMemo,

        },
        {
            id: 'date',
            Header: "Date",
            accessor: "date",
            className: "w-[10%]",
            Cell: (cell) => {
                return <Link 
                    target='_blank'
                    className='w-[5rem] flex' state={{ prevPath: location.state?.prevPath ?? "" }}
                    to={primaryMasterId === Constants.primaryMasterIds.peId ? routeNames.pe_investors_funds_detail_page(cell.value.id, cell.value.dealId) : routeNames.vc_investors_funds_detail_page(cell.value.id, cell.value.dealId)}>
                    {cell?.value?.date != '' ? moment(cell?.value?.date).format('MMM YYYY') : "--"}
                </Link>
            },
            sortType: dealDateSort
        },
        {
            Header: "Target Size (US$ M)",
            accessor: "targetSizeInUS",
            className: "text-right pr-[2.5rem] md:pr-[2.5rem] lg:pr-[2.75rem] w-[1%]",
            headerspanClassName: "ml-auto",
            Cell: (cell) => {
                return addCommasToNumber(cell?.value)
            },
        },
        // {
        //     Header: "Amount Raised (US$ M)",
        //     accessor: "amountRaisedInUS",
        //     className: "text-right lg:pr-[2.7rem] pr-[2.5rem]  md:pr-[3rem] w-[1%]",
        //     headerspanClassName: "ml-auto",
        //     Cell: (cell) => {
        //         return addCommasToNumber(cell?.value)
        //     },

        // },
        {
            Header: "Status",
            accessor: "status",
            className: "w-[14%] whitespace-nowrap"
        },
        {
            Header: "Capital Source",
            accessor: "captitalSource",
            className: "w-[13%]",
            headerClassName: "10%",
        },

    ], [])


   /* The `rowData` constant is using the `useMemo` hook to memoize the data that will be displayed in
   the data table. It iterates over the `funds` array and creates a new array of objects,
   where each object represents a row in the data table. Each object contains properties for the
   fund name, date, target size, amount raised, status, and capital source. */
    const rowData = useMemo(() => {
        let rows = [];
        funds?.forEach(fund => {
            rows.push({
                fundName: { name: fund?.fund?.name, dealId: fund?.id, id: fund.fund?.id },
                date: { date: fund?.date, id: investorId, dealId: fund?.id },
                targetSizeInUS: fund?.size == 0 ? "" : fund?.size,
                amountRaisedInUS: fund?.amount_raised == 0 ? "" : fund?.amount_raised,
                status: fund?.status,
                captitalSource: fund?.capital_source == 0 ? "" : fund?.capital_source,
            })
        });
        setshowXlDownload(rows.length != 0)
        return rows;

    }, [funds])
   /* The `initialSort` constant is using the `useMemo` hook to memoize an array of sorting options for
   the data table. It specifies that the data should be initially sorted by the `date` column in
   descending order (`desc: true`). The empty dependency array (`[]`) ensures that the sorting
   options are only calculated once and not re-evaluated on subsequent renders. */
    const initialSort = useMemo(() => [
        {
            id: 'date',
            desc: true
        }
    ], []);


    const pathSeg = location.pathname.split('/');

    const category = pathSeg.includes('exit')
        ? "exits"
        : pathSeg.includes('ipo')
            ? "ipos"
            : pathSeg.includes('angel')
                ? "angel"
                : pathSeg.includes('incubation')
                    ? "incubation"
                    : "investments";


    return (
        <> 
            {investorsFundsDataFound && <div className=" px-4 bg-[#EDEDED]" ref={InvFundsRef}>
                <div className=" mt-4 bg-white shadow-[0_2px_6px_0_rgba(0,0,0,0.08)] overflow-hidden sm:rounded-lg">
                    <div className="pt-4  px-5 border-b border-b-[#F1F1F1]">
                        <div className="flex justify-between mb-2">
                            <h3 className="text-[14pt] leading-[19pt] text-[#333333] lg:text-[17px] lg:leading-[22px] font-semibold mb-3">Funds</h3>
                            {!isDirectoryUser ? (isStudent == 'false' && isTrialExport !='false'  && showXlDownload) && <ExcelDownloader
                                url={exportExcel}
                                fileName={Constants.exportNames.portfolio_companies_funds(investorName)}
                                data={{
                                    "type":primaryMasterId,
                                    "export_from": "detail",
                                    "module_type":"investors",
                                    "category": category,
                                    "section":"funds",
                                   "investor_id": [investorId]
                                  }} /> : ""}
                        </div>
                    </div>


                    <div className="space-y-6">
                        <DataTable showLoader={isLoading} columns={columns} data={rowData} sortBy={initialSort} RefValue={InvFundsRef}/>
                    </div>

                </div>

            </div>}
        </>
    )
}

export default InvestorFunds